import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useFormState, useWatch } from "react-hook-form";
import { useGetApplicationApplicantDetailsSensitiveQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useGetApplicationApplicantDetailsSensitiveQuery";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useFetchPlaidUserAccountQuery } from "@lending/hooks/queries/Plaid/useFetchPlaidUserAccountQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useFlowNavigation } from "@lending/navigation";
import { APPLICATION_RELATIONSHIP, PREFILL_DATA_SOURCE } from "@lending/types";
import { useStore } from "../../../../../contexts/StoreContext";
import { createPersonalInfoReviewSchema } from "../createSchema";
import type { PersonalInfoReviewFormData } from "../components/PersonalInfoEditForm";

const defaultValues: PersonalInfoReviewFormData = {
  first_name: "",
  last_name: "",
  tax_id: "",
  date_of_birth: "",
  street: "",
  street2: "",
  city: "",
  region: "",
  postal_code: "",
};

function usePersonalInfoReviewForm() {
  const { l10n } = useLocalization();
  const { goToPath } = useFlowNavigation();
  const { data: storeData, updateData } = useStore();
  const { institution } = useGetInstitutionQuery();
  const [initialHasMissingFields, setInitialHasMissingFields] = useState<boolean | null>(null);

  const applicationUuid = useCurrentApplicationUuid();
  const { data: applicantResponse, isLoading: isApplicantLoading } =
    useListApplicationApplicantDetailsQuery();
  const applicant = applicantResponse?.results?.find(
    (a) => a.application_relationship === APPLICATION_RELATIONSHIP.PRIMARY
  );
  const { data: sensitiveApplicant } = useGetApplicationApplicantDetailsSensitiveQuery(
    applicant?.id ?? ""
  );

  const hasApplicant = Boolean(applicant);
  const plaidPublicToken = storeData?.plaidPublicToken ?? "";

  // Skip plaid fetch if we already have stored form data (saved on previous submit/back).
  // When plaidPiiFormData is undefined but plaidPublicToken exists, the user just
  // completed a fresh Plaid verification — always fetch in that case.
  const shouldSkipPlaidFetch =
    Boolean(storeData?.plaidPiiFormData) || (!plaidPublicToken && hasApplicant);

  const {
    data: plaidData,
    isLoading,
    isError,
  } = useFetchPlaidUserAccountQuery(shouldSkipPlaidFetch ? undefined : plaidPublicToken);

  const { control, reset, trigger, handleSubmit, getValues, setError } =
    useForm<PersonalInfoReviewFormData>({
      resolver: zodResolver(createPersonalInfoReviewSchema(l10n, institution)),
      mode: "all",
      defaultValues,
    });

  const identity = plaidData?.identity;

  // Hydrate form from the best available data source
  useEffect(() => {
    // Priority 1: Plaid identity data (fresh verification with new public token)
    if (identity) {
      const values = {
        first_name: identity.first_name ?? "",
        last_name: identity.last_name ?? "",
        tax_id: identity.ssn ?? "",
        date_of_birth: identity.date_of_birth ?? "",
        street: identity.address?.street ?? "",
        street2: identity.address?.street2 ?? "",
        city: identity.address?.city ?? "",
        region: identity.address?.region ?? "",
        postal_code: identity.address?.postal_code ?? "",
      };
      reset(values);
      void trigger().then((isValid) => {
        setInitialHasMissingFields(!isValid);
      });
      return;
    }

    // Priority 2: Store data (preserved from previous navigation)
    if (storeData.plaidPiiFormData) {
      const stored = storeData.plaidPiiFormData;
      const values = {
        first_name: stored.first_name,
        last_name: stored.last_name,
        tax_id: stored.tax_id,
        date_of_birth: stored.date_of_birth,
        street: stored.street,
        street2: stored.street2,
        city: stored.city,
        region: stored.region,
        postal_code: stored.postal_code,
      };
      reset(values);
      void trigger().then((isValid) => {
        setInitialHasMissingFields(!isValid);
      });
      return;
    }

    // Priority 3: Existing applicant from API (hydration after hard refresh)
    if (applicant) {
      const address = applicant.residential_address;

      const values = {
        first_name: applicant.first_name ?? "",
        last_name: applicant.last_name ?? "",
        tax_id: sensitiveApplicant?.tax_id ?? "",
        date_of_birth: sensitiveApplicant?.date_of_birth ?? "",
        street: address?.street_address ?? "",
        street2: address?.street_address_2 ?? "",
        city: address?.city ?? "",
        region: address?.region_code ?? "",
        postal_code: address?.postal_code ?? "",
      };
      reset(values);
      void trigger().then((isValid) => {
        setInitialHasMissingFields(!isValid);
      });

      // Hydrate phone/email into store so submit payload is correct
      if (!storeData.phone && applicant.phone) {
        updateData({ phone: applicant.phone, email: applicant.email ?? "" });
      }
    }
    // storeData.plaidPiiFormData, storeData.phone, and updateData are intentionally excluded:
    // plaidPiiFormData is a fallback source that should not trigger re-population
    // (saveFormDataToStore writes to it); phone/updateData are only used for one-time
    // hydration in Priority 3.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, reset, trigger, applicant, sensitiveApplicant]);

  // On fetch error, show empty form in missing-fields state. The user got a
  // public token but identity fetch failed — attribute as MANUAL so we don't
  // misreport this as a successful Plaid prefill.
  useEffect(() => {
    if (isError) {
      setInitialHasMissingFields(true);
      updateData({ prefillDataSource: PREFILL_DATA_SOURCE.MANUAL });
    }
  }, [isError, updateData]);

  // If there is no public token and no existing applicant, redirect to manual entry
  useEffect(() => {
    if (isApplicantLoading) return;
    if (!plaidPublicToken && !hasApplicant) {
      updateData({
        skippedPlaidPrefill: true,
        plaidPiiFormData: undefined,
        plaidPublicToken: undefined,
        prefillDataSource: PREFILL_DATA_SOURCE.MANUAL,
      });
      void goToPath("/personal-info");
    }
  }, [plaidPublicToken, hasApplicant, isApplicantLoading, updateData, goToPath]);

  const formValues = useWatch({ control });
  const { errors, dirtyFields } = useFormState({ control });

  return {
    control,
    handleSubmit,
    getValues,
    setError,
    formValues,
    errors,
    dirtyFields,
    initialHasMissingFields,
    isLoading,
    isSubmittingPlaidFetch: isLoading,
    identity,
    applicant,
    sensitiveApplicant,
    applicationUuid,
    institution,
  };
}

export { usePersonalInfoReviewForm };
